import { WorkflowStep, WorkflowInput } from '../types/workflow';
import { ProjectConfig } from '../context/ProjectContext';
import { parseFunctionSignatureWithInputs } from './functionSignatureParser';

/**
 * Updates system nodes with project configuration data
 */
export function updateSystemNodesWithProjectData(
  steps: WorkflowStep[], 
  project: ProjectConfig
): WorkflowStep[] {
  const chainConfig = project.chainConfig;
  
  return steps.map(step => {
    switch (step.name) {
      case 'prepare-authdata':
        return updatePrepareAuthDataNode(step, project);
      case 'target-calldata':
        return updateTargetCalldataNode(step, project);
      case 'sca-calldata':
        return updateScaCalldataNode(step, project);
      case 'bundle':
        return updateBundleNode(step, project);
      default:
        return step;
    }
  });
}

/**
 * Updates prepare-authdata node with project configuration
 */
function updatePrepareAuthDataNode(step: WorkflowStep, project: ProjectConfig): WorkflowStep {
  const chainConfig = project.chainConfig;
  
  // If project has a selected function, update signature and derived fields
  let updatedStep: WorkflowStep = {
    ...step,
    config: {
      ...step.config,
      rpc_url: chainConfig.rpcUrl,
      target_contract: project.contractAddress,
      function_signature: project.selectedFunction || step.config.function_signature,
      sender: step.config.sender || "0x3dB2eac708DCd7833dDc87d2f3A25eEde0621a55"
    }
  };

  return updatedStep;
}

/**
 * Updates target-calldata node with project functions
 */
function updateTargetCalldataNode(step: WorkflowStep, project: ProjectConfig): WorkflowStep {
  // Apply selected function if provided, and derive parameters/inputs
  const functionSignature = project.selectedFunction || step.config.function_signature;
  let updatedStep: WorkflowStep = {
    ...step,
    config: {
      ...step.config,
      function_signature: functionSignature
    }
  };

  if (typeof functionSignature === 'string' && functionSignature.trim()) {
    try {
      const { parameters, inputs } = parseFunctionSignatureWithInputs(functionSignature);
      updatedStep = {
        ...updatedStep,
        config: { ...updatedStep.config, parameters },
        inputs
      };
    } catch {}
  }

  return updatedStep;
}

/**
 * Updates sca-calldata node with execute function
 */
function updateScaCalldataNode(step: WorkflowStep, project: ProjectConfig): WorkflowStep {
  const chainConfig = project.chainConfig;
  
  // Update inputs with project contract address
  const inputsArray = Array.isArray(step.inputs) ? (step.inputs as WorkflowInput[]) : [];
  const updatedInputs = inputsArray.map((input: WorkflowInput) => {
    if (input.name === 'dest') {
      return {
        ...input,
        value: project.contractAddress
      };
    }
    return input;
  });

  return {
    ...step,
    inputs: Array.isArray(step.inputs) ? updatedInputs : step.inputs
  };
}

/**
 * Updates bundle node with chain configuration and contract data
 */
function updateBundleNode(step: WorkflowStep, project: ProjectConfig): WorkflowStep {
  const chainConfig = project.chainConfig;
  
  // Get chain-specific gas settings
  const gasSettings = getChainGasSettings(project.chainId);
  
  // Compute Pimlico endpoints if API key provided
  const pimlicoApiKey = project.pimlicoApiKey?.trim();
  const pimlicoNetworkSlug = getPimlicoNetworkSlug(project.chainId);
  const pimlicoUrls = pimlicoApiKey && pimlicoNetworkSlug ? {
    bundler_url: `https://api.pimlico.io/v2/${pimlicoNetworkSlug}/rpc?apikey=${pimlicoApiKey}`,
    paymaster_url: `https://api.pimlico.io/v2/${pimlicoNetworkSlug}/rpc?apikey=${pimlicoApiKey}`,
  } : {};

  return {
    ...step,
    config: {
      ...step.config,
      chain_id: project.chainId,
      sender: step.config.sender || "0x3dB2eac708DCd7833dDc87d2f3A25eEde0621a55", // Default sender
      rpc_url: chainConfig.rpcUrl,
      target_contract: project.contractAddress,
      // Update API URLs if key provided
      ...pimlicoUrls,
      ...gasSettings
    }
  };
}

/**
 * Gets chain-specific gas settings
 */
function getChainGasSettings(chainId: number): Record<string, string> {
  switch (chainId) {
    case 11155111: // Sepolia
      return {
        gas_limit: "100000",
        max_fee_per_gas: "20000000000", // 20 gwei
        max_priority_fee_per_gas: "2000000000" // 2 gwei
      };
    case 1: // Ethereum Mainnet
      return {
        gas_limit: "150000",
        max_fee_per_gas: "30000000000", // 30 gwei
        max_priority_fee_per_gas: "2000000000" // 2 gwei
      };
    case 137: // Polygon
      return {
        gas_limit: "100000",
        max_fee_per_gas: "50000000000", // 50 gwei
        max_priority_fee_per_gas: "30000000000" // 30 gwei
      };
    case 8453: // Base
      return {
        gas_limit: "100000", 
        max_fee_per_gas: "1000000000", // 1 gwei
        max_priority_fee_per_gas: "1000000000" // 1 gwei
      };
    case 42161: // Arbitrum One
      return {
        gas_limit: "300000",
        max_fee_per_gas: "1000000000", // 1 gwei
        max_priority_fee_per_gas: "1000000000" // 1 gwei
      };
    default:
      return {
        gas_limit: "100000",
        max_fee_per_gas: "20000000000",
        max_priority_fee_per_gas: "2000000000"
      };
  }
}

function getPimlicoNetworkSlug(chainId: number): string | null {
  switch (chainId) {
    case 1:
      return 'eth-mainnet';
    case 11155111:
      return 'sepolia';
    case 137:
      return 'polygon-mainnet';
    case 8453:
      return 'base-mainnet';
    case 42161:
      return 'arbitrum-one';
    default:
      return null;
  }
}

/**
 * Creates function signature dropdown options for system nodes - only functions with authData
 */
export function getFunctionSignatureOptions(project: ProjectConfig): Array<{
  value: string;
  label: string;
  description: string;
}> {
  if (!project.contractABI) {
    return [];
  }

  return project.contractABI.abi
    .filter(func => hasAuthDataParameter(func))
    .map(func => {
      const signature = generateFunctionSignature(func);
      const additionalParams = func.inputs.slice(1);
      
      return {
        value: signature,
        label: func.name,
        description: additionalParams.length > 0 ? 
          `Additional parameters: ${additionalParams.map(p => `${p.name} (${p.type})`).join(', ')}` : 
          'Only authData parameter'
      };
    });
}

/**
 * Checks if a function has authData as its first parameter
 */
function hasAuthDataParameter(func: any): boolean {
  if (func.inputs.length === 0) return false;
  
  const firstInput = func.inputs[0];
  if (firstInput.type !== 'tuple' || !firstInput.components) return false;
  
  // Check if first parameter matches authData structure exactly
  const expectedTypes = ['uint256', 'uint256', 'bytes32', 'tuple[]', 'bytes', 'bool', 'bytes'];
  const actualTypes = firstInput.components.map((comp: any) => comp.type);
  
  if (expectedTypes.length !== actualTypes.length) return false;
  
  // Verify each type matches
  for (let i = 0; i < expectedTypes.length; i++) {
    if (expectedTypes[i] !== actualTypes[i]) return false;
  }
  
  // For tuple[] (executions), verify the nested structure
  if (firstInput.components[3].type === 'tuple[]' && firstInput.components[3].components) {
    const executionTypes = firstInput.components[3].components.map((comp: any) => comp.type);
    const expectedExecutionTypes = ['bytes32', 'bytes', 'bytes'];
    if (executionTypes.length !== 3 || !executionTypes.every((type: string, idx: number) => type === expectedExecutionTypes[idx])) {
      return false;
    }
  }
  
  return true;
}

/**
 * Generates function signature from ABI function
 */
function generateFunctionSignature(func: any): string {
  const inputs = func.inputs.map((input: any) => {
    if (input.components && input.components.length > 0) {
      const tupleTypes = input.components.map((comp: any) => comp.type).join(',');
      return input.type.includes('[]') ? `(${tupleTypes})[]` : `(${tupleTypes})`;
    }
    return input.type;
  }).join(',');
  
  return `${func.name}(${inputs})`;
}